<template>
  <v-container>
    <div class="text-center" v-if="loading">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div v-else-if="clicked" @click="changeEditIcon">
      <div class="highlight" v-html="formattedQuery"></div>
    </div>
    <div v-else>
      <v-textarea
        outlined
        :loading="loading"
        :rules="requiredRules"
        :hint="hint"
        :label="label"
        :placeholder="placeholder"
        v-model="unformattedQuery"
        style="font-family:Consolas,Ubuntu Mono"
        @change="changeItem"
        @blur="confirmSqlChange"
      />
      <v-btn icon @click="confirmSqlChange" class="icon-textarea" color="primary">
        <v-icon>mdi-check</v-icon>
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import { requiredRules } from '../../utils/rules'
import SqlFormatter from '../../utils/SqlFormatter'

export default {
  name: 'SQLFormatter',

  data: () => ({
    unformattedQuery: '',
    formattedQuery: '',
    clicked: true,
    loading: false
  }),

  computed: {
    requiredRules () {
      return this.required && this.unformattedQuery === '' ? requiredRules(this.query) : []
    }
  },

  model: {
    prop: 'query',
    event: 'change'
  },

  props: {
    required: {
      type: Boolean,
      default: false
    },
    query: {
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    }
  },

  mounted () {
    this.unformattedQuery = this.query

    if (this.unformattedQuery) {
      this.formatSql()
    } else {
      this.clicked = false
      this.loading = false
    }
  },

  methods: {
    changeItem (event) {
      if (this.unformattedQuery) this.$emit('change', event)
    },

    changeEditIcon () {
      this.clicked = !this.clicked
    },

    confirmSqlChange () {
      if (this.unformattedQuery) this.formatSql()
    },

    formatSql () {
      if (this.unformattedQuery) {
        this.loading = true
        SqlFormatter(this.unformattedQuery)
          .then(response => {
            if (response?.data?.output) {
              this.formattedQuery = response.data.output
              this.clicked = true
            }
          })
          .catch(error => {
            this.$message.error(`${this.$t('warnings.error.occurred')} : ${error}`)
            this.clicked = false
          })
          .finally(() => { this.loading = false })
      }
    }
  }
}
</script>

<style>
.icon-textarea {
  margin-top: -70px;
  margin-right: 15px;
  float: right;
}

.formatter pre {
  font-family: "Source Code Pro", Monospace;
  font-size: 14pt;
}

code,
pre {
  height: 200px;
  padding: 0 3px 2px;
  font-family: Monaco, Menlo, Consolas, "Courier New", monospace;
  font-size: 12px;
  color: #333;
  -webkit-border-radius: 3px;
  -moz-border-radius: 3px;
  border-radius: 3px;
  overflow-y: auto;
}

code {
  padding: 2px 4px;
  color: #d14;
  background-color: #f7f7f9;
  border: 1px solid #e1e1e8;
  white-space: nowrap;
}

pre {
  display: block;
  padding: 9.5px;
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 20px;
  word-break: break-all;
  word-wrap: break-word;
  white-space: pre;
  white-space: pre-wrap;
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border: 1px solid rgba(0, 0, 0, 0.15);
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  border-radius: 4px;
}

pre.prettyprint {
  margin-bottom: 20px;
}

pre code {
  padding: 0;
  color: inherit;
  white-space: pre;
  white-space: pre-wrap;
  background-color: transparent;
  border: 0;
}

.c {
  color: #408080;
  font-style: italic;
} /* Comment */
.err {
  border: 1px solid #ff0000;
} /* Error */
.k {
  color: #008000;
  font-weight: bold;
} /* Keyword */
.o {
  color: #666666;
} /* Operator */
.cm {
  color: #408080;
  font-style: italic;
} /* Comment.Multiline */
.cp {
  color: #bc7a00;
} /* Comment.Preproc */
.c1 {
  color: #408080;
  font-style: italic;
} /* Comment.Single */
.cs {
  color: #408080;
  font-style: italic;
} /* Comment.Special */
.gd {
  color: #a00000;
} /* Generic.Deleted */
.ge {
  font-style: italic;
} /* Generic.Emph */
.gr {
  color: #ff0000;
} /* Generic.Error */
.gh {
  color: #000080;
  font-weight: bold;
} /* Generic.Heading */
.gi {
  color: #00a000;
} /* Generic.Inserted */
.go {
  color: #808080;
} /* Generic.Output */
.gp {
  color: #000080;
  font-weight: bold;
} /* Generic.Prompt */
.gs {
  font-weight: bold;
} /* Generic.Strong */
.gu {
  color: #800080;
  font-weight: bold;
} /* Generic.Subheading */
.gt {
  color: #0040d0;
} /* Generic.Traceback */
.kc {
  color: #008000;
  font-weight: bold;
} /* Keyword.Constant */
.kd {
  color: #008000;
  font-weight: bold;
} /* Keyword.Declaration */
.kp {
  color: #008000;
} /* Keyword.Pseudo */
.kr {
  color: #008000;
  font-weight: bold;
} /* Keyword.Reserved */
.kt {
  color: #b00040;
} /* Keyword.Type */
.m {
  color: #666666;
} /* Literal.Number */
.s {
  color: #ba2121;
} /* Literal.String */
.na {
  color: #7d9029;
} /* Name.Attribute */
.nb {
  color: #008000;
} /* Name.Builtin */
.nc {
  color: #0000ff;
  font-weight: bold;
} /* Name.Class */
.no {
  color: #880000;
} /* Name.Constant */
.nd {
  color: #aa22ff;
} /* Name.Decorator */
.ni {
  color: #999999;
  font-weight: bold;
} /* Name.Entity */
.ne {
  color: #d2413a;
  font-weight: bold;
} /* Name.Exception */
.nf {
  color: #0000ff;
} /* Name.Function */
.nl {
  color: #a0a000;
} /* Name.Label */
.nn {
  color: #0000ff;
  font-weight: bold;
} /* Name.Namespace */
.nt {
  color: #008000;
  font-weight: bold;
} /* Name.Tag */
.nv {
  color: #19177c;
} /* Name.Variable */
.ow {
  color: #aa22ff;
  font-weight: bold;
} /* Operator.Word */
.w {
  color: #bbbbbb;
} /* Text.Whitespace */
.mf {
  color: #666666;
} /* Literal.Number.Float */
.mh {
  color: #666666;
} /* Literal.Number.Hex */
.mi {
  color: #666666;
} /* Literal.Number.Integer */
.mo {
  color: #666666;
} /* Literal.Number.Oct */
.sb {
  color: #ba2121;
} /* Literal.String.Backtick */
.sc {
  color: #ba2121;
} /* Literal.String.Char */
.sd {
  color: #ba2121;
  font-style: italic;
} /* Literal.String.Doc */
.s2 {
  color: #ba2121;
} /* Literal.String.Double */
.se {
  color: #bb6622;
  font-weight: bold;
} /* Literal.String.Escape */
.sh {
  color: #ba2121;
} /* Literal.String.Heredoc */
.si {
  color: #bb6688;
  font-weight: bold;
} /* Literal.String.Interpol */
.sx {
  color: #008000;
} /* Literal.String.Other */
.sr {
  color: #bb6688;
} /* Literal.String.Regex */
.s1 {
  color: #ba2121;
} /* Literal.String.Single */
.ss {
  color: #19177c;
} /* Literal.String.Symbol */
.bp {
  color: #008000;
} /* Name.Builtin.Pseudo */
.vc {
  color: #19177c;
} /* Name.Variable.Class */
.vg {
  color: #19177c;
} /* Name.Variable.Global */
.vi {
  color: #19177c;
} /* Name.Variable.Instance */
.il {
  color: #666666;
} /* Literal.Number.Integer.Long */

.n {
  color: rgb(0, 69, 90);
}
.p {
  color: brown;
  font-weight: bold;
}
</style>
